import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { ProjectSettingsContext } from '../project-settings.context';
import { ProjectDependenciesDialogService } from './project-dependencies-dialog.service';
import { EditDependenciesModel } from './project-dependencies.model';
import { Observable } from 'rxjs';
import { ProjectBundle } from '../../projects/project.model';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { ParsedBundle, STANDARD_BUNDLE_INFO } from '../../resources/bundle-management/bundle.model';
import { ProjectDependenciesHelper } from './project-dependencies.helper';
import _ from 'lodash';

@Component({
    selector: 'adm4-project-dependencies',
    template: `
    <div>
      <div class="section-title">Standard libraries</div>
      <div class="table-container">
        @if (shouldDisplayBundleTableSection(standardProjectBundles$ | async)) {
          <adm4-project-common-dependencies
            [projectBundles]="standardProjectBundles$ | async"
          [isEditMode]="false"></adm4-project-common-dependencies>
        } @else {
          <div class='empty-library-message'>No library is assigned to this project</div>
        }
        <div class="footer">
          <div>
            @if (hasInvalidBundles(standardProjectBundles$ | async, projectSettingsContext.allBundles$ | async)) {
              <adm4-validation-message [isError]='true' [message]='DEPENDENCIES_NOT_AVAILABLE_TEXT'></adm4-validation-message>
            }
          </div>
          @if (!readOnly) {
            <div>
              <button (click)="openEditDialog()" class="admn4-button-ellipse-blue">Edit</button>
            </div>
          }
        </div>
      </div>
    </div>
    @if (shouldDisplayBundleTableSection(additionalProjectBundles$ | async)) {
      <div>
        <div class="section-title">Additional libraries</div>
        <div class="table-container">
          <adm4-project-dependencies-table
            [projectBundles]="additionalProjectBundles$ | async"
          [isEditMode]="false"></adm4-project-dependencies-table>
          <div class="footer">
            <div>
              @if (hasInvalidBundles(additionalProjectBundles$ | async, projectSettingsContext.allBundles$ | async)) {
                <adm4-validation-message [isError]='true' [message]='DEPENDENCIES_NOT_AVAILABLE_TEXT'></adm4-validation-message>
              }
            </div>
            @if (!readOnly) {
              <div>
                <button (click)="openEditDialog()" class="admn4-button-ellipse-blue">Edit</button>
              </div>
            }
          </div>
        </div>
      </div>
    }
    `,
    styleUrls: ['project-dependencies.component.scss'],
    standalone: false
})
export class ProjectDependenciesComponent implements OnChanges {
  projectSettingsContext = inject(ProjectSettingsContext);
  private projectDependenciesDialogService = inject(ProjectDependenciesDialogService);

  @Input() readOnly: boolean;
  @Input() projectKey: string;

  standardProjectBundles$: Observable<ProjectBundle[]>;
  additionalProjectBundles$: Observable<ProjectBundle[]>;

  readonly DEPENDENCIES_NOT_AVAILABLE_TEXT = 'Some of the libraries are not available anymore. Please update or remove them.';

  constructor() {
    this.standardProjectBundles$ = this.projectSettingsContext.displayProjectBundles$.pipe(
      map((projectBundles: ProjectBundle[]) => projectBundles.filter(projectBundle => ProjectDependenciesHelper.isProjectBundleStandard(projectBundle, STANDARD_BUNDLE_INFO)))
    );
    this.additionalProjectBundles$ = this.projectSettingsContext.displayProjectBundles$.pipe(
      ProjectDependenciesHelper.internalPluginsFilter,
      map((projectBundles: ProjectBundle[]) => projectBundles.filter(projectBundle => !ProjectDependenciesHelper.isProjectBundleStandard(projectBundle, STANDARD_BUNDLE_INFO)))
    );
    this.handleDependencyUpdateTrigger();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.projectKey && this.projectKey) {
      this.projectSettingsContext.loadProjectBundles(this.projectKey);
    }
  }

  shouldDisplayBundleTableSection(projectBundles: ProjectBundle[]): boolean {
    return !_.isEmpty(projectBundles);
  }

  hasInvalidBundles(projectBundles: ProjectBundle[], allBundles: ParsedBundle[]): boolean {
    return ProjectDependenciesHelper.isAnyProjectBundleInvalid(projectBundles, allBundles);
  }

  openEditDialog(): void {
    const editModel: EditDependenciesModel = {
      projectBundles$: this.projectSettingsContext.projectBundles$,
      projectKey: this.projectKey,
      onSaveCallback: () => this.reloadBundles()
    };
    this.projectDependenciesDialogService.openEditDialog(editModel);
  }

  reloadBundles(): void {
    this.projectSettingsContext.loadProjectBundles(this.projectKey);
    this.projectSettingsContext.reloadPatternTypes(this.projectKey);
    this.projectSettingsContext.reloadPropertyTypes(this.projectKey);
    this.projectSettingsContext.invalidatePluginDoc();
  }

  handleDependencyUpdateTrigger(): void {
    this.projectSettingsContext.allBundles$.pipe(
      withLatestFrom(this.projectDependenciesDialogService.openDependencyDialogTrigger))
      .pipe(take(1)).subscribe(() => this.openEditDialog());
  }
}
